import React from 'react';
import { EditorState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

import { ChevronRightIcon, MinusIcon } from '../../shared/Icons';
import { OPTION_TOOLBAR } from './constants';
import { MoveOptionButton, RemoveOptionButton, ValueInput } from './styles';
import { MultiSelectOptionItem } from './types';

interface OptionRowProps {
  option: MultiSelectOptionItem;
  index: number;
  isOpen: boolean;
  editorState: EditorState;
  canRemove: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  showImage: boolean;
  showExclusive: boolean;
  onToggle: (key: string) => void;
  onLabelChange: (index: number, editorState: EditorState) => void;
  onValueChange: (index: number, value: string) => void;
  onImageChange: (index: number, imageUrl: string) => void;
  onIconChange: (index: number, icon: string) => void;
  onExclusiveChange: (index: number, exclusive: boolean) => void;
  onRemove: (index: number) => void;
  onMove: (index: number, delta: number) => void;
}

const OptionRow: React.FC<OptionRowProps> = ({
  option,
  index,
  isOpen,
  editorState,
  canRemove,
  canMoveUp,
  canMoveDown,
  showImage,
  showExclusive,
  onToggle,
  onLabelChange,
  onValueChange,
  onImageChange,
  onIconChange,
  onExclusiveChange,
  onRemove,
  onMove,
}) => (
  <div className={`mso-row${isOpen ? ' is-open' : ''}`}>
    <div className="mso-row-head">
      <button
        type="button"
        className="mso-row-toggle"
        onClick={() => onToggle(option.key)}
        aria-expanded={isOpen}
        title={isOpen ? 'Collapse option' : 'Expand option'}
      >
        <span className={`mso-row-chevron${isOpen ? ' is-open' : ''}`}>
          <ChevronRightIcon size={14} />
        </span>
        <span className="mso-row-index">Option {index + 1}</span>
        {!isOpen &&
          (option.text ? (
            <span
              className="mso-row-preview"
              dangerouslySetInnerHTML={{
                __html: option.html || option.text,
              }}
            />
          ) : (
            <span className="mso-row-preview mso-row-preview-empty">
              Empty label
            </span>
          ))}
      </button>

      <div className="mso-row-actions">
        <MoveOptionButton
          type="button"
          onClick={() => onMove(index, -1)}
          disabled={!canMoveUp}
          title="Move up"
          aria-label="Move option up"
        >
          <ChevronRightIcon size={13} className="mso-rotate-up" />
        </MoveOptionButton>
        <MoveOptionButton
          type="button"
          onClick={() => onMove(index, 1)}
          disabled={!canMoveDown}
          title="Move down"
          aria-label="Move option down"
        >
          <ChevronRightIcon size={13} className="mso-rotate-down" />
        </MoveOptionButton>
        {canRemove && (
          <RemoveOptionButton
            type="button"
            onClick={() => onRemove(index)}
            title="Remove option"
            aria-label="Remove option"
          >
            <MinusIcon size={14} />
          </RemoveOptionButton>
        )}
      </div>
    </div>

    {isOpen && (
      <>
        <span className="mso-field-label">Label</span>
        <div className="mso-editor-shell">
          <Editor
            toolbar={OPTION_TOOLBAR}
            editorState={editorState}
            onEditorStateChange={(next: EditorState) =>
              onLabelChange(index, next)
            }
            stripPastedStyles
          />
        </div>

        <span className="mso-field-label">Value</span>
        <ValueInput
          type="text"
          placeholder="Stored value"
          value={option.value || ''}
          onChange={(event) => onValueChange(index, event.target.value)}
        />

        <span className="mso-field-label">Icon (optional)</span>
        <ValueInput
          type="text"
          className="mso-icon-input"
          placeholder="e.g. 😌"
          maxLength={8}
          value={option.icon || ''}
          onChange={(event) => onIconChange(index, event.target.value)}
        />

        {showExclusive && (
          <label className="mso-check">
            <input
              type="checkbox"
              checked={Boolean(option.exclusive)}
              onChange={(event) => onExclusiveChange(index, event.target.checked)}
            />
            Clears other picks (e.g. "None of the above")
          </label>
        )}

        {showImage && (
          <>
            <span className="mso-field-label">Image URL (optional)</span>
            <div className="mso-image-field">
              {option.imageUrl?.trim() && (
                <img
                  className="mso-image-thumb"
                  src={option.imageUrl.trim()}
                  alt=""
                />
              )}
              <ValueInput
                type="url"
                placeholder="https://example.com/image.png"
                value={option.imageUrl || ''}
                onChange={(event) => onImageChange(index, event.target.value)}
              />
            </div>
          </>
        )}
      </>
    )}
  </div>
);

export default OptionRow;
